/* eslint-disable camelcase */
import logger from '@/lib/clientLogger';
import { FeatureFlags } from '@/components/FeatureFlagsContext';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import { CLIENT_FEATURE_FLAGS } from '@/constants';
import getVertical from './verticalUtils';

/**
 * Posts an Amplitude event with the specified data.
 * Additional fields can be sent using the `extraData` parameter. The fields in that object will be
 * added to the `data` object that will be posted to Amplitude.
 * The minimal data sent is the following:
 * {
 *   name: [type],
 *   data: {
 *     cta_clicked: cta,
 *     final_step: isFinalStep, // default false
 *     vertical: getVertical() // gets vertical from URL
 *     [(job|enrollment)_flow]: flow,
 *     [(job|enrollment)_step]: step,
 *   }
 * }
 * @see AnalyticsHelper.logEvent
 * @param type Event type. Currently 'Member Enrolled' or 'Job Posted'
 * @param step Flow step the event was generated in.
 * @param flow Flow name the event was generated in.
 * @param extraData Any additional key-value pairs that should be sent besides the default data
 * @param cta CTA clicked. Defaults to 'next'
 * @param isFinal boolean indicating if this is the flow's last step. Defaults to 'false'
 */

export type MemberEnrolledEvent = 'Member Enrolled' | 'Member Enrolled - Seeker';
export type JobPostedEvent = 'Job Posted';
export type EnrollmentStep =
  | 'Grade'
  | 'Modes'
  | 'Dates'
  | 'Intent'
  | 'Details'
  | 'Tasks'
  | 'Service type'
  | 'Location'
  | 'Caregiver tasks'
  | 'Age level'
  | 'Email'
  | 'First and Last Name'
  | 'Password'
  | 'day care account creation - name'
  | 'day care account creation - contact'
  | 'SingleEnrollment'
  | 'Additional Verticals'
  | 'Account creation'
  | 'Sign up';

export type JobStep =
  | 'PAJ - Start'
  | 'PAJ - Schedule'
  | 'PAJ - Additional Needs'
  | 'PAJ - Ideal caregiver'
  | 'PAJ - Extra info'
  | 'PAJ - Goals'
  | 'PAJ - Finish';

export type EventType = MemberEnrolledEvent | JobPostedEvent;
export type EventStep = EnrollmentStep | JobStep;

export type MemberOverallStatus =
  | 'imcomplete'
  | 'active'
  | 'active_basic'
  | 'new'
  | 'closed'
  | 'active_lite'
  | 'active_snooze'
  | 'active_premium'
  | 'free_trial_premium'
  | 'active_featured'
  | 'inactive'
  | 'stale';

export function logCareEvent(
  type: JobPostedEvent,
  step: JobStep,
  extraData?: Record<string, any>,
  cta?: string,
  isFinal?: boolean,
  flow?: string
): void;
export function logCareEvent(
  type: MemberEnrolledEvent,
  step: EnrollmentStep,
  extraData?: Record<string, any>,
  cta?: string,
  isFinal?: boolean,
  flow?: string
): void;
export function logCareEvent(
  type: EventType,
  step: EventStep,
  extraData: Record<string, any> = {},
  cta: string = 'Next',
  isFinal: boolean = false,
  flow: string = 'MW VHP'
): void {
  let stepKey;
  let flowKey;
  let hideFinalStep = false;
  switch (type) {
    case 'Member Enrolled':
      stepKey = 'enrollment_step';
      flowKey = 'enrollment_flow';
      break;
    case 'Member Enrolled - Seeker':
      stepKey = 'enrollment_step';
      flowKey = 'enrollment_flow';
      hideFinalStep = extraData.screen_name === 'Sign up' && !isFinal;
      break;
    case 'Job Posted':
      stepKey = 'job_step';
      flowKey = 'job_flow';
      break;
    default:
      throw new Error(`Invalid event type ${type}`);
  }

  AnalyticsHelper.logEvent({
    name: type,
    data: {
      cta_text: cta,
      ...(!hideFinalStep && { final_step: isFinal }),
      vertical: getVertical(),
      [stepKey]: step,
      [flowKey]: flow,
      ...extraData,
    },
  });
}

// If you need more custom data for the screen viewed event, just add the type here

interface ScreenViewedCustomData {
  screen_name?: string;
  screen_template?: string;
  need_email?: boolean;
  member_overall_status?: MemberOverallStatus;
  num_of_caregivers?: number;
  caregivers_have_photo?: number;
  caregivers_have_review?: number;
  caregivers_have_social_badge?: number;
  version?: string;
  subheadline?: string;
  featured_attribute?: string;
  caregiverCount?: number;
}

export const setDataByURL = (url: string): ScreenViewedCustomData => {
  const signUp = /\/signup$/;
  const careAttributes = /\/care-attributes$/;
  const serviceType = /\/service-type$/;
  const location = /\/location$/;
  const careLocation = /\/care-location$/;
  const caregiverRecap = /\/caregiver-recap$/;
  const accountCreationPassword = /\/account-creation\/password$/;

  // if url starts with /seeker and ends with /location or /care-location
  if (url.startsWith('/seeker') && (location.test(url) || careLocation.test(url))) {
    return {
      screen_name: 'Care Location',
    };
  }

  // if url starts with /seeker and ends with /care-attributes
  if (url.startsWith('/seeker') && careAttributes.test(url)) {
    return {
      screen_name: 'Caregiver Attributes',
    };
  }

  // if url starts with /seeker and ends with /caregiver-recap
  if (url.startsWith('/seeker') && caregiverRecap.test(url)) {
    return {
      screen_name: 'Caregiver Recap',
    };
  }

  // if url starts with /seeker and ends with /signup
  if (url.startsWith('/seeker') && signUp.test(url)) {
    return {
      screen_name: 'Sign up',
    };
  }

  // if url starts with /seeker and ends with /account-creation/password
  if (url.startsWith('/seeker') && accountCreationPassword.test(url)) {
    return {
      screen_name: 'Set password',
    };
  }

  // if url starts with /seeker and ends with /service-type
  if (url.startsWith('/seeker') && serviceType.test(url)) {
    return {
      screen_name: 'Service Type',
    };
  }

  switch (url) {
    case '/seeker/sc':
      return {
        screen_name: 'seeker.enroll.consolidatedFlow.seniorcare.updateGuided.intro',
        screen_template: 'seeker.enroll.consolidatedFlow.seniorcare.updateGuided.intro',
      };
    case '/seeker/sc/care-recipient':
      return {
        screen_name: 'Recipient Details',
      };
    case '/seeker/sc/care-who':
      return {
        screen_name: 'Care Recipient',
      };
    case '/seeker/cc/children':
      return {
        screen_name: 'Child Count',
      };
    case '/seeker/hk':
      return {
        screen_name: 'Cleaning Type',
      };
    case '/seeker/hk/space':
      return {
        screen_name: 'Space Size',
      };
    case '/seeker/hk/about':
      return {
        screen_name: 'About Space',
      };
    case '/seeker/cc/v2/care-kind':
      return {
        screen_name: 'Care Kind',
      };
    default:
      return {};
  }
};

export function logScreenViewedEvent(
  customData: ScreenViewedCustomData,
  url: string,
  ldFlags: FeatureFlags
) {
  const dataByUrl = setDataByURL(url);
  const data = { ...dataByUrl, ...customData };
  let eventName = 'Screen Viewed';

  if (url.startsWith('/seeker')) {
    eventName = 'Screen Viewed - Seeker Enrollment';
  }

  AnalyticsHelper.logEvent({
    name: eventName,
    data: {
      ...data,
      referer: document.referrer,
    },
  });

  if (
    url.startsWith('/seeker') &&
    ldFlags[CLIENT_FEATURE_FLAGS.GROWTH_SEEKER_SCREEN_VIEWED_SPLUNK]?.value
  ) {
    logger.info({
      ...data,
      event: eventName,
      referer: document.referrer,
    });
  }
}
